import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { FiArrowUpRight, FiImage, FiPackage } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import TopBanner from "../../../components/topBanner/topBanner";
import TypicalProjectSlider from "../typicalProject/TypicalProjectSlider";
import { getEcosystemGroupsByLanguage } from "../../../apis/adminEcosystemGroups";
import { normalizeUploadedImageUrl } from "../../../apis/uploadImage";
import { getConsultingsByLanguage } from "../../../apis/consultings";
import { getProductSolutionCategories } from "../../../apis/productSolutionCategories";
import HsvCorpOverview from "./HsvCorpOverview";
import HsvCommerceOverview from "./HsvCommerceOverview";
import { normalizeLang } from "../../../utils/localeDataLoader";
import bannerImg from "../../../images/banner/banner---gioi-thieu.png";
import serviceHospitalImg from "../../../images/ConslutingService/tu-van-dau-tu-phong-kham.jpg";
import serviceSystemImg from "../../../images/ConslutingService/image-tich-hop-he-thong-2.jpeg";
import serviceDataImg from "../../../images/ConslutingService/asset-v1_FUNiX+DAP303x_01-A_VN+2022_T5+type@asset+block@Data-Engineer_Blog-scaled.jpeg";
import serviceOperationImg from "../../../images/ConslutingService/suachuabaotrimaymocdungquydinh.jpg";
import servicePlatformImg from "../../../images/ConslutingService/he-thong-tin.png";
import hospitalImg from "../../../images/product_solution/hospital.png";
import hospitalSoftwareImg from "../../../images/product_solution/phan-mem-quan-ly-benh-vien.jpg";
import hospitalEmrImg from "../../../images/product_solution/emr-ihospital.jpg";
import clinicImg from "../../../images/product_solution/clinic.png";
import clinicSoftwareImg from "../../../images/product_solution/phan-mem-quan-ly-phong-kham-mien-phi-banner.jpg";
import smartHospitalImg from "../../../images/product_solution/smart-hospital-emr.png";
import cloudImg from "../../../images/product_solution/banner-cloud-services-slider.jpg";
import serverImg from "../../../images/product_solution/banner-server.jpg";
import communicationImg from "../../../images/product_solution/banner-giaiphap-communication.jpg";
import newsBannerImg from "../../../images/banner/banner-tin-tuc.jpg";
import newsEventImg from "../../../images/news/events/05115022025.png";
import newsTechnologyImg from "../../../images/news/events/chatgpt_openai_png.png";
import educationImg from "../../../images/news/active/2015_07_30_Junxiaos_PhD_defense.png";
import customerServiceImg from "../../../images/ourservices/services-img1.jpg";
import customerOperationImg from "../../../images/ourservices/services-img2.jpg";
import customerSupportImg from "../../../images/ourservices/services-img3.jpg";
import "./ecosystem-detail.scss";

const responseItems = (response) =>
  response?.data?.items || response?.items || response?.data || [];

const normalizeText = (value = "") =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\u0111/g, "d")
    .replace(/\u0110/g, "D")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .toLowerCase();

const classifyEcosystemItem = (category) => {
  const text = normalizeText(
    `${category.name || ""} ${category.description || ""} ${category.groupName || ""} ${category.slug || ""}`,
  );

  // "Phần mềm giáo dục" phải thuộc Đào tạo trước khi xét từ khóa phần mềm.
  if (/giao duc|dao tao|hoc tap|learning/.test(text)) return "education";

  if (/phan mem|software/.test(text)) return "technology";

  if (
    /truyen thong|communication|collaboration|noi dung|media|vcdn|cdn|giong noi|speech/
      .test(text)
  ) {
    return "pharma";
  }

  if (
    /ha tang|onsite|tai cho|bao tri|bao hanh|service desk|servicedesk|dich vu khach hang|may chu|server|luu tru|data center|cloud|dam may|vcloud|sao luu|bao mat|ha tang mang|quan ly mang|ao hoa/
      .test(text)
  ) {
    return "investment";
  }

  if (/benh vien/.test(text) && /phong kham/.test(text)) return "healthcare";
  if (/phong kham|clinic/.test(text)) return "chuoi-phong-kham";
  if (/benh vien|hospital|y te|health|medical/.test(text)) return "healthcare";

  // Thương mại nhận toàn bộ dữ liệu còn lại.
  return "infrastructure";
};

const toDatabaseService = (item, source, { isEnglish, groupCode } = {}) => {
  const explicitUrl =
    item.url ||
    item.link ||
    item.detailUrl ||
    item.websiteUrl ||
    item.path ||
    "";
  let link = explicitUrl;

  if (!link) {
    if (source === "category") {
      const basePath = isEnglish ? "products-solutions" : "san-pham-giai-phap";
      const targetGroup = item.groupSlug || groupCode || "";
      const targetSlug = item.slug || "";
      if (targetSlug) {
        link = targetGroup
          ? `/${basePath}/${targetGroup}/${targetSlug}`
          : `/${basePath}/${targetSlug}`;
      } else if (targetGroup) {
        link = `/${basePath}/${targetGroup}`;
      } else {
        link = `/${basePath}`;
      }
    } else if (source === "consulting") {
      const basePath = isEnglish ? "investment-services" : "dau-tu-dich-vu";
      const targetSlug = item.slug || item.urlSlug || "";
      link = targetSlug ? `/${basePath}/${targetSlug}` : "";
    }
  }

  return {
    id: `${source}-${item.id}`,
    title: item.name || item.description,
    subtitle:
      item.description && item.description !== item.name
        ? item.description
        : "",
    description: item.description || "",
    image: item.image || "",
    sortOrder: Number(item.sortOrder) || 0,
    source,
    link,
  };
};

// Ảnh fallback được ghép trực tiếp với đúng nội dung từ source cũ.
// Không dùng index/modulo vì thứ tự thay đổi sẽ làm ảnh chạy sang card khác.
const ECOSYSTEM_SERVICES = {
  healthcare: [
    { vi: "Tư vấn đầu tư, thiết kế và xây dựng bệnh viện", en: "Hospital investment, design and construction consulting", image: serviceHospitalImg },
    { vi: "Triển khai hệ thống quản lý bệnh viện HIS, EMR và PACS", en: "HIS, EMR and PACS implementation", image: hospitalSoftwareImg },
    { vi: "Tích hợp hạ tầng công nghệ, thiết bị và dữ liệu y tế", en: "Healthcare infrastructure, equipment and data integration", image: hospitalEmrImg },
    { vi: "Tư vấn vận hành, chuyển giao công nghệ và đào tạo nhân sự", en: "Operations consulting, technology transfer and training", image: serviceOperationImg },
    { vi: "Cung ứng dược phẩm, vật tư và thiết bị y tế", en: "Pharmaceutical, medical supply and equipment distribution", image: hospitalImg },
  ],
  "chuoi-phong-kham": [
    { vi: "Tư vấn xây dựng và chuẩn hóa mô hình chuỗi phòng khám", en: "Clinic chain design and standardization", image: clinicImg },
    { vi: "Phần mềm quản lý phòng khám và hồ sơ sức khỏe điện tử", en: "Clinic management and electronic health records", image: clinicSoftwareImg },
    { vi: "Đặt lịch, tiếp đón, thanh toán và chăm sóc khách hàng", en: "Appointment, reception, payment and customer care", image: smartHospitalImg },
    { vi: "Kết nối xét nghiệm, chẩn đoán hình ảnh và nhà thuốc", en: "Laboratory, imaging and pharmacy integration", image: serviceSystemImg },
    { vi: "Đào tạo vận hành và kiểm soát chất lượng dịch vụ", en: "Operations training and service quality control", image: serviceOperationImg },
  ],
  education: [
    { vi: "Đào tạo chuyển giao phần mềm và quy trình vận hành", en: "Software and operating process transfer training", image: educationImg },
    { vi: "Đào tạo quản trị bệnh viện và cơ sở y tế", en: "Hospital and healthcare administration training", image: serviceHospitalImg },
    { vi: "Phát triển nội dung học tập số", en: "Digital learning content development", image: serviceDataImg },
    { vi: "Tổ chức chương trình đào tạo theo nhu cầu doanh nghiệp", en: "Customized enterprise training programs", image: customerSupportImg },
  ],
  technology: [
    { vi: "Phần mềm quản lý bệnh viện, phòng khám và nhà thuốc", en: "Hospital, clinic and pharmacy management software", image: hospitalSoftwareImg },
    { vi: "Hồ sơ bệnh án điện tử và nền tảng dữ liệu y tế", en: "Electronic medical records and healthcare data platforms", image: hospitalEmrImg },
    { vi: "Tích hợp hệ thống, API và chuyển đổi số", en: "System integration, APIs and digital transformation", image: serviceSystemImg },
    { vi: "Hạ tầng máy chủ, điện toán đám mây và an toàn thông tin", en: "Server, cloud and cybersecurity infrastructure", image: serverImg },
    { vi: "Telehealth, IoT và các giải pháp MedTech", en: "Telehealth, IoT and MedTech solutions", image: cloudImg },
  ],
  investment: [
    { vi: "Tư vấn và vận hành dịch vụ chăm sóc khách hàng", en: "Customer care consulting and operations", image: customerServiceImg },
    { vi: "Tổ chức trung tâm hỗ trợ và tiếp nhận đa kênh", en: "Omnichannel support and reception centers", image: customerOperationImg },
    { vi: "Quản lý lịch hẹn, phản hồi và trải nghiệm khách hàng", en: "Appointment, feedback and customer experience management", image: customerSupportImg },
    { vi: "Kết nối dịch vụ y tế tại nhà và dịch vụ theo yêu cầu", en: "Home healthcare and on-demand service connectivity", image: servicePlatformImg },
  ],
  pharma: [
    { vi: "Truyền thông thương hiệu và nội dung y tế", en: "Healthcare brand communication and content", image: communicationImg },
    { vi: "Quản trị nội dung số, tin tức và thư viện truyền thông", en: "Digital content, news and media library management", image: newsBannerImg },
    { vi: "Tổ chức chiến dịch truyền thông đa kênh", en: "Omnichannel communication campaigns", image: newsEventImg },
    { vi: "Truyền thông nội bộ và quan hệ đối tác", en: "Internal communications and partner relations", image: newsTechnologyImg },
  ],
  infrastructure: [
    { vi: "Cho thuê và vận hành hạ tầng dùng chung", en: "Shared infrastructure leasing and operations", image: serviceOperationImg },
    { vi: "Cung ứng nền tảng thương mại cho y tế và doanh nghiệp", en: "Healthcare and enterprise commerce platforms", image: servicePlatformImg },
    { vi: "Quản lý tài sản, thiết bị và chuỗi cung ứng", en: "Asset, equipment and supply chain management", image: serverImg },
    { vi: "Kết nối đối tác, phân phối và dịch vụ hậu cần", en: "Partner, distribution and logistics connectivity", image: serviceSystemImg },
  ],
};

function EcosystemDetail() {
  const { slug: rawSlug } = useParams();
  const location = useLocation();
  const slug = rawSlug || location.pathname.split('/').filter(Boolean).pop() || "";
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);
  const isEnglish = lang === "en";
  const [groups, setGroups] = useState([]);
  const [consultings, setConsultings] = useState([]);
  const [solutionCategories, setSolutionCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [slug]);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);

    Promise.all([
      getEcosystemGroupsByLanguage(lang),
      getConsultingsByLanguage(lang),
      getProductSolutionCategories(lang),
    ])
      .then(([groupResponse, consultingItems, categoryItems]) => {
        if (!mounted) return;
        setGroups(responseItems(groupResponse));
        setConsultings(Array.isArray(consultingItems) ? consultingItems : []);
        setSolutionCategories(Array.isArray(categoryItems) ? categoryItems : []);
      })
      .catch((error) => {
        console.error("Không thể tải dữ liệu hệ sinh thái:", error);
        if (mounted) {
          setConsultings([]);
          setSolutionCategories([]);
        }
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [lang]);

  const group = useMemo(() => {
    if (!groups || groups.length === 0) return null;
    const targetNorm = normalizeText(slug || "");
    const cleanSlugStr = (slug || "").toLowerCase().replace(/_/g, "-");
    const isSpecialCommerce = cleanSlugStr === "hsv-thuong-mai" || cleanSlugStr === "hsv-commercial" || cleanSlugStr === "hsv-thuongmai";
    
    // Special check for HSV Thương Mại: display identical to HSV Phần mềm (technology)
    if (isSpecialCommerce) {
      const techMatched = groups.find((item) => {
        const itemText = normalizeText(`${item.code || ""} ${item.slug || ""} ${item.name || ""} ${item.title || ""}`);
        return itemText.includes("phan mem") || itemText.includes("technology") || item.code === "technology";
      });
      if (techMatched) return techMatched;
    }

    // 1. Direct match by slug or code
    let matched = groups.find(
      (item) => item.slug === slug || item.code === slug
    );
    if (matched) return matched;

    // 2. Normalized match by slug, code or name
    matched = groups.find((item) => {
      const itemSlugNorm = normalizeText(item.slug || "");
      const itemCodeNorm = normalizeText(item.code || "");
      const itemNameNorm = normalizeText(item.name || item.title || "");
      return itemSlugNorm === targetNorm || itemCodeNorm === targetNorm || itemNameNorm === targetNorm;
    });
    if (matched) return matched;

    // 3. Smart semantic category matching
    matched = groups.find((item) => {
      const itemText = normalizeText(`${item.code || ""} ${item.slug || ""} ${item.name || ""} ${item.title || ""}`);
      if (targetNorm.includes("phong kham") && itemText.includes("phong kham")) return true;
      if ((targetNorm.includes("dao tao") || targetNorm.includes("giao duc")) && (itemText.includes("dao tao") || itemText.includes("giao duc") || itemText.includes("education"))) return true;
      if (targetNorm.includes("benh vien") && (itemText.includes("benh vien") || itemText.includes("healthcare"))) return true;
      if ((targetNorm.includes("phan mem") || targetNorm.includes("cong nghe")) && (itemText.includes("phan mem") || itemText.includes("technology"))) return true;
      if (targetNorm.includes("truyen thong") && (itemText.includes("truyen thong") || itemText.includes("pharma") || itemText.includes("media"))) return true;
      if (targetNorm.includes("thuong mai") && itemText.includes("thuong mai")) return true;
      if ((targetNorm.includes("cham soc khach hang") || targetNorm.includes("dich vu")) && (itemText.includes("dich vu") || itemText.includes("investment"))) return true;
      return false;
    });
    if (matched) return matched;

    // 4. Fallback to code mapping if available
    if (targetNorm.includes("phong kham")) {
      matched = groups.find(g => g.code === "chuoi-phong-kham" || g.code === "healthcare");
    } else if (targetNorm.includes("dao tao") || targetNorm.includes("giao duc")) {
      matched = groups.find(g => g.code === "education");
    }
    if (matched) return matched;

    return groups[0];
  }, [groups, slug]);

  const services = useMemo(() => {
    const groupCode = group?.code;
    if (!groupCode) return [];

    const dbGroupServices =
      group?.services || group?.EcosystemServices || group?.ecosystemServices || [];
    if (dbGroupServices.length > 0) {
      const fallbackList = ECOSYSTEM_SERVICES[groupCode] || [];
      return dbGroupServices.map((item, index) => {
        const fallbackImg = fallbackList[index]?.image || "";
        const explicitLink = item.detailUrl || item.link || item.url || "";
        return {
          id: item.id || `${groupCode}-dbservice-${index}`,
          title: item.title || item.name || "",
          subtitle: item.description || "",
          description: item.description || "",
          image: normalizeUploadedImageUrl(item.imageUrl || item.image || fallbackImg),
          sortOrder: item.sortOrder || index + 1,
          source: item.serviceType || "ecosystem-service",
          link: explicitLink,
        };
      });
    }

    const consultingServices = consultings
      .filter((item) => item.isActive !== false)
      .filter((item) => classifyEcosystemItem(item) === groupCode)
      .map((item) => toDatabaseService(item, "consulting", { isEnglish, groupCode }));

    const categoryServices = solutionCategories
      .filter((item) => item.isActive !== false)
      .filter((item) => classifyEcosystemItem(item) === groupCode)
      .map((item) => toDatabaseService(item, "category", { isEnglish, groupCode }));

    const databaseServices = [...consultingServices, ...categoryServices].sort(
      (a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title),
    );

    if (databaseServices.length > 0) return databaseServices;

    return (ECOSYSTEM_SERVICES[groupCode] || []).map((service, index) => {
      return {
        id: `${groupCode}-fallback-${index}`,
        title: service[isEnglish ? "en" : "vi"],
        description: "",
        image: service.image || "",
        sortOrder: index + 1,
        source: "fallback",
        link: service.link || service.url || "",
      };
    });
  }, [consultings, group, isEnglish, solutionCategories]);

  const isHsvCommerce = useMemo(() => {
    const cleanSlugStr = (slug || "").toLowerCase().replace(/_/g, "-");
    return (
      cleanSlugStr === "hsv-thuong-mai" ||
      cleanSlugStr === "hsv-commercial" ||
      cleanSlugStr === "hsv-thuongmai"
    );
  }, [slug]);

  // Individual products (product solution categories) belonging to this ecosystem group
  const products = useMemo(() => {
    if (isHsvCommerce) {
      const fromGroup = (group?.products || []).filter((product) => {
        const normGroupSlug = normalizeText(product.groupSlug || "");
        const normGroupName = normalizeText(product.groupName || "");
        return (
          normGroupSlug.includes("san pham cong ty") ||
          normGroupSlug.includes("company") ||
          normGroupName.includes("san pham cong ty") ||
          normGroupName.includes("company")
        );
      });
      if (fromGroup.length > 0) return fromGroup;

      return solutionCategories.filter((category) => {
        const normGroupSlug = normalizeText(category.groupSlug || "");
        const normGroupName = normalizeText(category.groupName || "");
        return (
          normGroupSlug.includes("san pham cong ty") ||
          normGroupSlug.includes("company") ||
          normGroupName.includes("san pham cong ty") ||
          normGroupName.includes("company")
        );
      });
    }
    return group?.products || [];
  }, [group, isHsvCommerce, solutionCategories]);

  const projectPath = isEnglish ? "/typical-projects" : "/du-an-tieu-bieu";

  const cleanSlug = (slug || "").toLowerCase().replace(/_/g, "-");
  if (cleanSlug === "hsv-corp" || cleanSlug === "hsvcorp" || cleanSlug === "hsv-corp-holding") {
    return <HsvCorpOverview isEnglish={isEnglish} />;
  }

  if (isLoading) {
    return (
      <main className="ecosystem-detail ecosystem-detail--empty">
        <div className="layout_container">
          <div className="ecosystem-detail__skeleton" />
        </div>
      </main>
    );
  }

  if (!group) {
    return (
      <main className="ecosystem-detail ecosystem-detail--empty">
        <div className="layout_container">
          <h1>{isEnglish ? "Content is being updated" : "Đang cập nhật dữ liệu"}</h1>
        </div>
      </main>
    );
  }

  return (
    <main className="ecosystem-detail">
      <TopBanner
        titleCenter={
          isHsvCommerce
            ? (isEnglish ? "HSV Commercial" : "HSV Thương Mại")
            : (group?.name || "")
        }
        imgBanner={bannerImg}
        pageName={isEnglish ? "Ecosystem" : "Hệ sinh thái"}
        text={
          isHsvCommerce
            ? (isEnglish ? "HSV Commercial" : "HSV Thương Mại")
            : (group?.name || "")
        }
      />

      <section className="layout_container ecosystem-detail__services">
        <div className="ecosystem-detail__heading">
          <div>
            <h2>
              {isHsvCommerce
                ? (isEnglish ? "Services under HSV Commercial" : "Dịch vụ thuộc HSV thương mại")
                : (isEnglish ? "Services in this ecosystem" : "Dịch vụ thuộc hệ sinh thái")}
            </h2>
          </div>
        </div>

        {services.length > 0 ? (
          <ol className="ecosystem-detail__service-list">
            {services.map((service, index) => {
              const numberStr = String(index + 1).padStart(2, "0");
              const handleCardClick = () => {
                if (!service.link) return;
                if (/^https?:\/\//i.test(service.link)) {
                  window.open(service.link, "_blank", "noopener,noreferrer");
                } else {
                  navigate(service.link);
                }
              };

              const isExternal = /^https?:\/\//i.test(service.link || "");

              return (
                <li
                  key={service.id}
                  className={`ecosystem-service-card ${service.link ? "ecosystem-service-card--clickable" : ""}`}
                  onClick={handleCardClick}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleCardClick();
                    }
                  }}
                  role={service.link ? "link" : undefined}
                  tabIndex={service.link ? 0 : undefined}
                >
                  <div className="ecosystem-service-card__header">
                    <span className="ecosystem-service-card__badge">
                      <span className="ecosystem-service-card__badge-dot" />
                      {isEnglish ? "Ecosystem Service" : "Dịch vụ Hệ sinh thái"}
                    </span>
                    <span className="ecosystem-service-card__number">#{numberStr}</span>
                  </div>

                  <div className="ecosystem-service-card__image-box">
                    {service.image ? (
                      <img
                        src={service.image}
                        alt={service.title}
                        onError={(event) => {
                          event.currentTarget.hidden = true;
                          event.currentTarget.nextElementSibling?.removeAttribute("hidden");
                        }}
                      />
                    ) : null}
                    <div
                      className="ecosystem-detail__image-placeholder"
                      hidden={Boolean(service.image)}
                    >
                      <FiImage />
                      <small>
                        {isEnglish
                          ? "Image is being updated"
                          : "Hình ảnh đang cập nhật"}
                      </small>
                    </div>
                  </div>

                  <div className="ecosystem-service-card__content">
                    <h3>
                      {service.link ? (
                        <Link
                          to={service.link}
                          target={isExternal ? "_blank" : undefined}
                          rel={isExternal ? "noopener noreferrer" : undefined}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {service.title}
                        </Link>
                      ) : (
                        service.title
                      )}
                    </h3>
                    {service.subtitle && service.subtitle !== service.title ? (
                      <p className="ecosystem-service-card__desc">{service.subtitle}</p>
                    ) : null}
                    <div className="ecosystem-service-card__footer">
                      {service.link ? (
                        <Link
                          to={service.link}
                          target={isExternal ? "_blank" : undefined}
                          rel={isExternal ? "noopener noreferrer" : undefined}
                          className="ecosystem-service-card__cta-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="ecosystem-service-card__cta-text">
                            {isEnglish ? "Explore solution" : "Khám phá dịch vụ"}
                          </span>
                          <span className="ecosystem-service-card__arrow">
                            <FiArrowUpRight />
                          </span>
                        </Link>
                      ) : (
                        <>
                          <span className="ecosystem-service-card__cta-text">
                            {isEnglish ? "Explore solution" : "Khám phá dịch vụ"}
                          </span>
                          <span className="ecosystem-service-card__arrow">
                            <FiArrowUpRight />
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="ecosystem-service-card__accent-bar" />
                </li>
              );
            })}
          </ol>
        ) : (
          <p className="ecosystem-detail__empty-service">
            {isEnglish
              ? "Service information is being updated."
              : "Thông tin dịch vụ đang được cập nhật."}
          </p>
        )}
      </section>

      {products.length > 0 && (
        <section className="ecosystem-detail__products-section">
          <div className="layout_container">
            <div className="ecosystem-detail__heading">
              <div>
                <h2>{isEnglish ? "Products & Solutions" : "Sản phẩm & Giải pháp"}</h2>
              </div>
              <Link to={isEnglish ? "/products-solutions" : "/san-pham-giai-phap"}>
                {isEnglish ? "View all" : "Xem tất cả"} <FiArrowUpRight />
              </Link>
            </div>
            <div className="ecosystem-detail__products-grid">
              {products.map((product, index) => {
                const targetUrl = product.productUrl || product.productLink || product.websiteUrl;
                const basePath = isEnglish ? "products-solutions" : "san-pham-giai-phap";
                const link = targetUrl || (product.slug
                  ? `/${basePath}/${product.groupSlug || ''}${product.groupSlug ? '/' : ''}${product.slug}`
                  : `/${basePath}`);
                const isExternal = /^https?:\/\//i.test(link);
                return (
                  <Link
                    key={product.id || index}
                    to={link}
                    target={isExternal ? "_blank" : undefined}
                    rel={isExternal ? "noopener noreferrer" : undefined}
                    className="eco-product-card"
                  >
                    <div className="eco-product-card__img">
                      {product.image ? (
                        <img src={normalizeUploadedImageUrl(product.image)} alt={product.name} />
                      ) : (
                        <div className="eco-product-card__img-fallback">
                          <FiPackage />
                        </div>
                      )}
                    </div>
                    <div className="eco-product-card__body">
                      {product.groupName && (
                        <span className="eco-product-card__group">{product.groupName}</span>
                      )}
                      <h3>{product.name}</h3>
                      {product.description && (
                        <p>{product.description}</p>
                      )}
                      <div className="eco-product-card__footer">
                        <span>{isEnglish ? "Learn more" : "Xem chi tiết"}</span>
                        <FiArrowUpRight />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="ecosystem-detail__projects">
        <div className="layout_container">
          <div className="ecosystem-detail__heading ecosystem-detail__heading--projects">
            <div>
              <h2>{isEnglish ? "Projects in this ecosystem" : "Dự án thuộc hệ sinh thái"}</h2>
            </div>
            <Link to={`${projectPath}?ecosystemGroupId=${group.id}`}>
              {isEnglish ? "View all projects" : "Xem tất cả dự án"} <FiArrowUpRight />
            </Link>
          </div>
          <TypicalProjectSlider
            ecosystemGroupId={group.id}
            variant="ecosystem"
            showAllProjects
          />
        </div>
      </section>
    </main>
  );
}

export default EcosystemDetail;
